import type { Bet } from 'src/types/domain/Bet'
import { betStatusMap } from 'src/types/domain/BetStatus'

/**
 * True when, at race settlement, a bet was never acknowledged by the server and
 * so must be purged + refunded rather than settled.
 *
 * A real bet — winner OR loser — receives `bet:accepted` at PLACEMENT, which
 * removes it from `unacceptedBets` there and then. So a bet still in
 * `unacceptedBets` at settlement was never registered server-side (a silent
 * failure: the placement never reached the server, or the ack was lost).
 *
 * We additionally require PENDING status, so a bet the server DID settle — e.g.
 * a `bet:won` that arrived even though its `bet:accepted` was lost in transit —
 * is treated as real and kept. Losses carry no server event, but a real loser
 * still left `unacceptedBets` at placement via its `bet:accepted`, so it is not
 * flagged here.
 *
 * Settling such a bet locally would render it as a phantom "won $0" in the
 * chip-placement panel and the bet history; purging avoids that and returns the
 * optimistically-deducted stake.
 */
export const isUnregisteredAtSettlement = (
  bet: Pick<Bet, 'id' | 'status'>,
  unacceptedBets: Record<string, unknown>
): boolean =>
  unacceptedBets[bet.id] !== undefined && bet.status === betStatusMap.PENDING
